// Where somebody is speaking in a recording: loudness in steps of 10 ms, with a threshold that adapts to the recording (a quiet noisy room and a studio differ).

export const FPS = 100;

// samples: Float32Array of a mono recording (-1..1) at `rate` Hz. Returns { fps, voiced: Uint8Array (1 = speech), level: Float32Array (loudness per step) }.
export function activityOf(samples, rate = 16000) {
  const step = Math.max(1, Math.round(rate / FPS));
  const count = Math.floor(samples.length / step);
  const level = new Float32Array(count);
  for (let i = 0; i < count; i += 1) {
    let sum = 0;
    for (let k = i * step; k < (i + 1) * step; k += 1) sum += samples[k] * samples[k];
    level[i] = Math.sqrt(sum / step);
  }
  // the quiet floor (the lowest tenth of the steps) and the speaking level (the loudest tenth): speech is whatever is well above the floor
  const sorted = Array.from(level).sort((a, b) => a - b);
  const at = (p) => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))] || 0;
  const floor = at(0.1);
  const top = at(0.92);
  const threshold = Math.max(0.004, floor + 0.18 * Math.max(0, top - floor));
  const voiced = new Uint8Array(count);
  for (let i = 0; i < count; i += 1) voiced[i] = level[i] > threshold ? 1 : 0;
  // gaps shorter than 60 ms inside speech are closed (a consonant is not a pause); blips shorter than 40 ms are dropped
  closeRuns(voiced, 0, 6);
  closeRuns(voiced, 1, 4);
  return { fps: FPS, voiced, level };
}

// Turns every run of `value` shorter than `shorter` steps into the other value (runs at the very ends are left).
function closeRuns(array, value, shorter) {
  let i = 0;
  while (i < array.length) {
    if (array[i] !== value) { i += 1; continue; }
    let j = i;
    while (j < array.length && array[j] === value) j += 1;
    if (j - i < shorter && i > 0 && j < array.length) for (let k = i; k < j; k += 1) array[k] = 1 - value;
    i = j;
  }
}

// A 16-bit mono WAV file of Float32 samples.
export function encodeWav(samples, rate = 16000) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const text = (offset, s) => { for (let i = 0; i < s.length; i += 1) view.setUint8(offset + i, s.charCodeAt(i)); };
  text(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  text(8, "WAVE");
  text(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  text(36, "data");
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}
